// src/api/launches.js
//
// Thin client for the proxy's launch-workspace REST endpoints
// (proxy-server/apiServer.js — "Launch workspace" section, backed by
// proxy-server/launches.js). Mirrors the private request() helper in
// api/checks.js — same base-URL resolution (REACT_APP_PROXY_API_URL in prod,
// falls back to a bare "/proxy" path relative to the page origin locally).
//
//   GET    /proxy/launches            -> { launches: [{ id, title, ticket, app,
//                                          build, updatedAt, counts:{criteria,runs} }] }
//   POST   /proxy/launches  { launch } -> { ok, launch } (create or update by launch.id)
//                                          400 { error:'invalid_launch' } | { error:'invalid_json', detail }
//   GET    /proxy/launches/<id>       -> { launch } | 404 { error:'not_found' }
//   GET    /proxy/launches/<id>/checks -> { suite, checks:[Check] } — the full
//                                          definition of every compiled check
//                                          (edit via appendCheckToSuite in api/checks.js)
//   DELETE /proxy/launches/<id>       -> { ok } (also deletes the paired launch-<id> user suite)
//                                          404 { error:'not_found' }
//   POST   /proxy/launches/<id>/attach-run { runId } -> { ok, launch }
//                                          404 { error:'not_found' } | 400 { error:'invalid_run_id' }
//   POST   /proxy/launches/<id>/compile { rows?, app?, adOnly?, criteriaText?,
//                                          scopeTypes?, scopeHosts? }
//                                        -> { ok, launch } | { ok:false, issues:[...] }
//                                          404 { error:'not_found' }
//                                          409 { error:'anthropic_api_key_missing', detail }
//   GET    /proxy/launches/<id>/rollup?source=merged|batch|live&detail=1
//                                        -> { launch, source, runIds, runAt, verdict,
//                                             signable, unsignableReason, rollup, coverage,
//                                             blockers[], criteria[]? } — WP6,
//                                          docs/mcp-signoff-spec.md §6/§9. The AUTHORITATIVE
//                                          server-side sign-off verdict for a launch, computed
//                                          from its recorded/attached check runs
//                                          (proxy-server/launch/buildRollup.js) — replaces the
//                                          frontend's own criterionStatus()/launchRollup() calls
//                                          for anything driving what LaunchPage DISPLAYS as the
//                                          verdict (those utils remain for CapturePanel's live
//                                          in-flight coverage and the HTML report — see the note
//                                          atop utils/launchRollup.js).
//
// A "launch" binds a feature's pasted acceptance criteria to the checks that
// verify them, the runs exercised against it, and an eventual sign-off — see
// docs/launch-signoff-spec.md §5. Compiling criteria into checks (WP3,
// §6/§7) is an LLM-backed route on the proxy (conformance/compileCriteria.js)
// — same 409 anthropic_api_key_missing gate as /proxy/checks/compile.
//
// Response shapes are echoed back to the caller as-is (parsed JSON) — the
// page does its own defensive optional-chaining on the fields it reads.

const PROXY_API_BASE = (process.env.REACT_APP_PROXY_API_URL || '/proxy').replace(/\/$/, '');

/**
 * Internal: POST/GET/DELETE JSON against the proxy REST API. Always resolves
 * the parsed JSON body on 2xx. On a non-2xx response, rejects with an Error
 * whose `.status` and `.body` carry the server's status code and parsed
 * error body (e.g. { error: 'not_found' }), so callers can branch on the
 * specific error code rather than just a message. Mirrors api/checks.js's
 * request() exactly.
 */
async function request(path, init = {}) {
  let res;
  try {
    // Routes live under /proxy/launches* on the proxy REST API. PROXY_API_BASE
    // is the host (no /proxy), so prepend it here — matches api/checks.js.
    res = await fetch(`${PROXY_API_BASE}/proxy${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(init.headers || {}),
      },
    });
  } catch (e) {
    const err = new Error(`Could not reach the proxy server at ${PROXY_API_BASE}`);
    err.status = 0;
    err.body = null;
    err.cause = e;
    throw err;
  }

  let json = null;
  try { json = await res.json(); } catch { /* empty/non-JSON body */ }

  if (!res.ok) {
    const err = new Error((json && (json.detail || json.error)) || `${init.method || 'GET'} ${path} failed (${res.status})`);
    err.status = res.status;
    err.body = json;
    throw err;
  }
  return json;
}

/**
 * GET /proxy/launches -> { launches: [{ id, title, ticket, app, build,
 * updatedAt, counts:{criteria,runs} }] } (slim — no criteria/signoff detail;
 * fetch getLaunch(id) for that). Returns [] on a malformed/empty response.
 */
export async function listLaunches() {
  const data = await request('/launches', { method: 'GET' });
  return Array.isArray(data?.launches) ? data.launches : [];
}

/**
 * GET /proxy/launches/<id> -> the full launch record, or null on a 404 /
 * malformed response (404 is swallowed to null rather than thrown, since
 * "not found" is a normal, expected outcome for this lookup — mirrors
 * getKnowledgePacks()'s catch-and-degrade contract in api/checks.js).
 */
export async function getLaunch(id) {
  try {
    const data = await request(`/launches/${encodeURIComponent(id)}`, { method: 'GET' });
    return data && typeof data === 'object' && data.launch ? data.launch : null;
  } catch (e) {
    if (e && e.status === 404) return null;
    throw e;
  }
}

/**
 * GET /proxy/launches/<id>/checks -> { suite, checks:[Check] } — the FULL
 * definition of every check the launch's compile produced (its own
 * `launch-<id>` suite: authored checks plus verbatim copies of every cited
 * existing check). The launch record itself only stores checkIds, so this is
 * what lets the page SHOW what a criterion actually asserts rather than just
 * naming it.
 *
 * Degrades to { suite:'', checks:[] } on a 404 / malformed response — a launch
 * that hasn't been compiled yet has no suite, which is a normal state, not an
 * error (same catch-and-degrade contract as getLaunch()).
 *
 * To EDIT one of these, POST it back through appendCheckToSuite(suite, check)
 * in api/checks.js — that route replaces an existing check with the same id.
 */
export async function getLaunchChecks(id) {
  try {
    const data = await request(`/launches/${encodeURIComponent(id)}/checks`, { method: 'GET' });
    return {
      suite: (data && typeof data.suite === 'string') ? data.suite : '',
      checks: Array.isArray(data?.checks) ? data.checks : [],
    };
  } catch (e) {
    if (e && e.status === 404) return { suite: '', checks: [] };
    throw e;
  }
}

/**
 * POST /proxy/launches { launch } -> { ok, launch } (create or update by
 * launch.id — omit id to create). On a 400 the promise rejects with
 * err.body = { error: 'invalid_launch' } or { error: 'invalid_json', detail }.
 */
export async function saveLaunch(launch) {
  return request('/launches', {
    method: 'POST',
    body: JSON.stringify({ launch }),
  });
}

/**
 * DELETE /proxy/launches/<id> -> { ok }. Also deletes the paired launch-<id>
 * user suite on the proxy. Rejects with err.status 404 when the launch is
 * unknown.
 */
export async function deleteLaunch(id) {
  return request(`/launches/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

/**
 * POST /proxy/launches/<id>/attach-run { runId } -> { ok, launch }
 * Records a check-run id (from runChecks()'s response — see api/checks.js)
 * against the launch, deduping and keeping only the newest 20. Rejects with
 * err.status 404 when the launch is unknown.
 */
export async function attachRun(id, runId) {
  return request(`/launches/${encodeURIComponent(id)}/attach-run`, {
    method: 'POST',
    body: JSON.stringify({ runId }),
  });
}

/**
 * POST /proxy/launches/<id>/runs { run } -> { ok, launch }
 * Evidence snapshot for one recorded run: { runId, at, mode, build, harId,
 * harError, missing, criteria:[{id, graded, failed}] }. Upserts by runId and
 * attaches it (so it replaces attachRun for runs that carry evidence). See
 * docs/ticket-validation-workflow-spec.md §4.
 */
export async function recordLaunchRun(id, run) {
  try {
    return await request(`/launches/${encodeURIComponent(id)}/runs`, {
      method: 'POST',
      body: JSON.stringify({ run }),
    });
  } catch (e) {
    // Pages and the droplet deploy separately: against a proxy that predates
    // /runs this 404s, and the run must still be attached. attach-run 404s
    // too when the launch itself is gone, so a real not-found still throws.
    if (e?.status === 404 && run?.runId) return attachRun(id, run.runId);
    throw e;
  }
}

/**
 * POST /proxy/launches/<id>/compile { rows?, app?, adOnly?, criteriaText? }
 * -> { ok, launch } — splits `criteriaText` (or the launch's already-saved
 * criteriaText when omitted) into criteria, maps each to existing checks /
 * authors a new one / falls back to a manual procedure, and persists the
 * result onto the launch (WP3, docs/launch-signoff-spec.md §6). `rows` is
 * the same scope shape callers pass to runChecks()/estimateScope() — the
 * grounding traffic the compiler profiles to decide existing-vs-authored.
 *
 * `scopeTypes` / `scopeHosts` describe WHICH requests the tester explicitly
 * picked (the Launch page's scope picker) rather than merely which rows were
 * sent. The proxy treats them as authoritative: an authored check is scoped to
 * exactly those hosts, so a criterion verified against one vendor's requests
 * can't come back reporting on another's. Omit them for "the whole session".
 *
 * Non-2xx does NOT throw for the two response shapes callers need to branch
 * on directly:
 *   - 404 { error:'not_found' }               -> rejects (unknown launch)
 *   - 409 { error:'anthropic_api_key_missing' } -> rejects (proxy needs a key)
 *   - 200 { ok:false, issues:[...] }           -> resolves normally; caller
 *     checks `.ok` itself (mirrors compile's own { ok:false } shape rather
 *     than a thrown error, since it's a normal "couldn't compile this" outcome)
 */
export async function compileLaunch(id, { rows, app, adOnly, criteriaText, scopeTypes, scopeHosts } = {}) {
  return request(`/launches/${encodeURIComponent(id)}/compile`, {
    method: 'POST',
    body: JSON.stringify({
      ...(Array.isArray(rows) ? { rows } : {}),
      ...(app ? { app } : {}),
      ...(adOnly ? { adOnly: true } : {}),
      ...(criteriaText != null ? { criteriaText } : {}),
      ...(Array.isArray(scopeTypes) && scopeTypes.length ? { scopeTypes } : {}),
      ...(Array.isArray(scopeHosts) && scopeHosts.length ? { scopeHosts } : {}),
    }),
  });
}

/**
 * GET /proxy/launches/<id>/rollup?source=&detail= -> the server-computed
 * sign-off rollup (WP6, docs/mcp-signoff-spec.md §6/§9):
 *   { launch, source, runIds, runAt, verdict, signable, unsignableReason,
 *     rollup:{met,notMet,needsReview,manualReview,notRun},
 *     coverage:{covered,partial,waiting,manual,unmapped,complete},
 *     blockers[], criteria[]? }
 * `source` — 'batch' | 'live' | 'merged' (proxy default 'merged' when
 * omitted). LaunchPage deliberately never passes 'merged' — it fetches
 * 'batch' and 'live' separately and keeps them labelled rather than letting
 * the proxy pick a winner (see LaunchPage.jsx's `sources` comment). `detail`
 * (boolean) requests the full per-criterion `criteria[]` breakdown; omitted
 * or false, the response carries only the launch-level rollup/coverage/blockers.
 *
 * Never 404s into a thrown error for a launch with zero runs — the proxy
 * itself returns 200 with `verdict:'not-run'`, `signable:false`, empty
 * `runIds` for that case (buildRollup.js). A genuinely unknown launch id
 * still rejects with err.status === 404, same as getLaunch()/getLaunchChecks().
 */
export async function getLaunchRollup(id, { source, detail } = {}) {
  const params = new URLSearchParams();
  if (source) params.set('source', source);
  if (detail) params.set('detail', '1');
  const qs = params.toString();
  return request(`/launches/${encodeURIComponent(id)}/rollup${qs ? `?${qs}` : ''}`, { method: 'GET' });
}

/**
 * POST /proxy/launches/<id>/review { action:'start'|'sign' } -> { ok, review }.
 * Review-time measurement (docs/validation-autopilot-spec.md §6): 'start' when a human
 * opens the launch (first call wins), 'sign' when they generate/download the sign-off report.
 * Best-effort — resolves null on any failure so measurement never blocks the page.
 */
export async function markLaunchReview(id, action) {
  try {
    return await request(`/launches/${encodeURIComponent(id)}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action }),
    });
  } catch { return null; }
}

/** GET /proxy/launches/review-summary -> { count, medianMinutes } (null on failure). */
export async function getReviewSummary() {
  try { return await request('/launches/review-summary', { method: 'GET' }); } catch { return null; }
}
